import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { Avatar, DatePicker, formatPickerDate } from '@scripsteam/scrips-signal-ds';
import { Search, ChevronLeft, ChevronRight, ChevronDown, ArrowRight } from 'lucide-react';
import {
  useProviderSlots,
  type ProviderSlotsResponse,
} from '@/services/scheduling/use-provider-slots';
import {
  usePreBookAppointmentProfile,
  pickDefaultReminder,
  pickAttachedBillingProfile,
  reminderRules,
  formatBillingProfile,
} from '@/services/scheduling/use-prebook-appointment-profile';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import { NO_SLOT_AVAILABILITY_MESSAGE } from '@/scheduling/lib/appointment-strings';
import { filterProvidersForCurrentUser } from '@/scheduling/lib/provider-self-filter';
import { useAuthStore } from '@/auth/auth-store';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { AppointmentSlot } from '@/types/scheduling-api';
import { practiceNow } from '@/shared/lib/practice-time';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface ProviderSelectionViewProps {
  /** Closes the view without changes. */
  onCancel: () => void;
  /**
   * Opens the Time & Date view. Mirrors Flutter
   * `calendar_screen.dart:676–680` — `onMore` sets the provider + first
   * profile, then pushes the dateTimeSelection step.
   */
  onOpenTimeDate: () => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Trigger + DS footer share this exact format so the two never drift. */
const DATE_FORMAT = 'MMMM dd, yyyy';

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getStripDays(anchor: Date) {
  return Array.from({ length: 5 }, (_, i) => {
    const d = startOfDay(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

function fmt12h(iso: string) {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'p' : 'a';
  return `${h > 12 ? h - 12 : h || 12}:${m}${ampm}`;
}

function fmtSlotDate(iso: string) {
  const d = new Date(iso);
  return `${MONTH_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

function getInitials(name: string) {
  const parts = name.replace(/^Dr\.?\s*/i, '').split(' ').filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

function formatWorkingHours(hours: { startTime?: string; endTime?: string }[] | undefined): string {
  if (!hours || hours.length === 0) return 'Non working';
  const compact = (t?: string) => (t ? t.toLowerCase().slice(0, -1).replace(/\s+/g, '') : '');
  const start = compact(hours[0].startTime);
  const end = compact(hours[0].endTime);
  if (!start || !end) return 'Non working';
  return `${start} - ${end}`;
}

/**
 * ProviderSelectionView — full-content replacement of the create-appointment
 * panel. Mirrors Flutter `provider_selection_widget.dart`:
 *
 *   - First / Follow Up tab → drives `appointmentProfileType` in the request
 *   - Autofocus search input (server-side, 600ms debounce, min 2 chars)
 *   - 5-day rolling date strip + tappable date picker header
 *   - Provider list with avatar / name / specialty / hours and 2 inline
 *     slot chips (date + time + location) + a forward arrow for "more"
 *   - Slot tap → picks provider + first profile + slot, returns to create
 *   - Forward arrow → picks provider + first profile only, returns to create
 *
 * Backed by POST /api/Doctor/ProviderSlots with `slotCount: 2`,
 * `searchSlot: true`, and `appointmentProfileType` set.
 */
export function ProviderSelectionView({ onCancel, onOpenTimeDate }: ProviderSelectionViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const formDate = watch('selectedDate');
  const location = watch('location');

  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';

  // Local state — Flutter's _ProviderSelectionWidgetState. Tab labels are
  // "First" / "Follow Up" but the wire enum (EnumAppointmentProfileType)
  // takes ROUTINE / FOLLOWUP — Flutter app_constants.dart:691.
  const [tab, setTab] = useState<'ROUTINE' | 'FOLLOWUP'>('ROUTINE');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 600);
  const effectiveSearch =
    debouncedSearch.length === 0 || debouncedSearch.length >= 2 ? debouncedSearch : null;

  const today = startOfDay(practiceNow());
  const initialDate = formDate ? startOfDay(new Date(formDate)) : today;
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  // Anchor at today so the strip layout matches the create-panel's
  // DatePickerSection. If the picked date falls outside [anchor, anchor+4],
  // the effect below shifts to keep the highlight visible.
  const [anchorDate, setAnchorDate] = useState<Date>(today);

  // Keep local selectedDate aligned with the form when the form value
  // changes — the create-panel writes `selectedDate` whenever the user
  // taps a date tile or uses the date dropdown, and we need that to
  // flow into the search view's strip + query.
  useEffect(() => {
    if (!formDate) return;
    const fd = startOfDay(new Date(formDate));
    setSelectedDate((prev) => (isSameDay(prev, fd) ? prev : fd));
  }, [formDate]);

  useEffect(() => {
    setAnchorDate((prev) => {
      const prevEnd = new Date(prev);
      prevEnd.setDate(prev.getDate() + 4);
      const startsBefore = selectedDate.getTime() < prev.getTime();
      const endsAfter = selectedDate.getTime() > prevEnd.getTime();
      return startsBefore || endsAfter ? selectedDate : prev;
    });
  }, [selectedDate]);

  const stripDays = getStripDays(anchorDate);
  // Today is the floor — past dates aren't bookable.
  const minDate = startOfDay(practiceNow());

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const locationId = location?.id;
  const queryParams = useMemo(() => {
    if (!practiceId || !orgId || !locationId || effectiveSearch === null) return null;
    // No `as const` — the hook's param interface takes mutable
    // string[]/string values; the readonly literal shape narrows the
    // tuple types and breaks structural compat.
    return {
      organizationId: orgId,
      practiceId,
      date: selectedDate.toISOString(),
      locationIds: [locationId],
      searchText: effectiveSearch,
      slotCount: 2,
      searchSlot: true,
      appointmentProfileType: tab,
    };
  }, [orgId, practiceId, selectedDate, locationId, effectiveSearch, tab]);

  const { data: allProviders = [], isLoading } = useProviderSlots(queryParams);

  // PROD-1024 (F-SCH-132): self-filter the provider list for a doctor/nurse —
  // they book only themselves. Flutter create_appointment_widget.dart:2202-2210.
  const selfFilterUser = useAuthStore((s) => s.user);
  const providers = useMemo(
    () => filterProvidersForCurrentUser(allProviders, selfFilterUser),
    [allProviders, selfFilterUser],
  );

  const preBook = usePreBookAppointmentProfile();

  // Auto-pick the first profile and PreBook it; matches Flutter
  // `calendar_screen.dart:643` (fetchPreSelectedApptProfileDetails after
  // both onSelection and onMore set the profile).
  const applyProviderAndProfile = (provider: ProviderSlotsResponse) => {
    setValue('providerId', provider.id, { shouldValidate: true });
    setValue('providerName', provider.name);
    setValue('providerSpeciality', specialityDisplay(provider.speciality) ?? null);
    const sCode = provider.speciality?.code as
      | { code?: string; display?: string; displayName?: string }
      | undefined;
    setValue(
      'providerSpecialityCode',
      sCode?.code
        ? {
            specialitySkillId: provider.specialityId,
            code: {
              code: sCode.code,
              displayName: sCode.displayName ?? sCode.display,
              system: 'https://snomed.info/sct',
              description: null,
              shortName: null,
            },
          }
        : null,
    );

    const firstProfile = provider.appointmentProfile?.[0];
    if (firstProfile) {
      setValue('appointmentProfileId', firstProfile.id ?? null, { shouldValidate: true });
      setValue('appointmentProfileName', firstProfile.profileName ?? null);
      setValue('appointmentProfileColor', firstProfile.color ?? null);
      // appointmentType (PascalCase tolerant) — same logic as
      // appointment-profile-section so the BookAppointment payload
      // doesn't ship `null` here.
      const at = firstProfile.appointmentType as
        | (typeof firstProfile.appointmentType & {
            Code?: string;
            Display?: string;
            Definition?: string;
          })
        | undefined;
      const atCode = at?.code ?? at?.Code;
      const atDisplay = at?.display ?? at?.Display;
      setValue(
        'appointmentType',
        atCode && atDisplay
          ? {
              code: atCode,
              display: atDisplay,
              definition: at?.definition ?? at?.Definition ?? '',
            }
          : null,
        { shouldValidate: true },
      );
      // reasonCode (PascalCase tolerant) — backend sometimes ships
      // `ReasonCode` capitalised, so reading `.reasonCode` returns
      // undefined and the schema's `reasonCode.code required` rule trips
      // with no obvious cause.
      const rc = firstProfile.reasonCode as
        | (typeof firstProfile.reasonCode & {
            Code?: string;
            Display?: string;
            Definition?: string;
          })
        | undefined;
      const rcCode = rc?.code ?? rc?.Code;
      const rcDisplay = rc?.display ?? rc?.Display;
      setValue(
        'reasonCode',
        rcCode && rcDisplay
          ? {
              code: rcCode,
              display: rcDisplay,
              definition: rc?.definition ?? rc?.Definition ?? '',
            }
          : null,
        // shouldValidate so the schema re-runs against the new value;
        // without this, validation may have run on an empty reasonCode
        // and left a stale "required" error.
        { shouldValidate: true },
      );
      setValue('minutesDuration', firstProfile.minutesDuration ?? null);
      setValue('pickedProfile', firstProfile);

      if (firstProfile.id && provider.id) {
        preBook.mutate(
          {
            organizationId: orgId,
            practiceId,
            providerId: provider.id,
            appointmentProfileId: firstProfile.id,
            date: selectedDate.toISOString(),
          },
          {
            onSuccess: (response) => {
              const reminder = pickDefaultReminder(response.reminderList);
              if (reminder?.id) {
                setValue('reminderProfileId', reminder.id, { shouldValidate: true });
                setValue('reminderProfileName', reminder.profileName ?? null);
                setValue('reminderProfileRules', reminderRules(reminder));
              }
              const billing = pickAttachedBillingProfile(firstProfile, response.billingProfiles);
              if (billing?.id) {
                setValue('billingProfileId', billing.id);
                setValue('billingProfileName', formatBillingProfile(billing));
              }
              if (response.providerReasonForVisits?.length) {
                setValue(
                  'reasonCodeOptions',
                  response.providerReasonForVisits.map((r) => ({
                    code: r.code,
                    display: r.display,
                    definition: r.definition,
                  })),
                );
              }
            },
          },
        );
      }
    }
  };

  // Selection — picks provider + first profile + slot.
  // Mirrors Flutter calendar_screen.dart:600–645 (onSelection).
  const onSelection = (provider: ProviderSlotsResponse, slot: AppointmentSlot) => {
    applyProviderAndProfile(provider);

    if (slot.start && slot.end) {
      setValue('slotId', `${provider.id}-${slot.start}`, { shouldValidate: true });
      setValue('slotStart', slot.start);
      setValue('slotEnd', slot.end);
      setValue('selectedDate', slot.start);
    }

    onCancel();
  };

  // Forward arrow — picks provider + first profile, then pushes the
  // Time & Date view (Flutter `calendar_screen.dart:646–681` —
  // CreateAppointmentStartEvent(dateTimeSelection)).
  const onMore = (provider: ProviderSlotsResponse) => {
    applyProviderAndProfile(provider);
    onOpenTimeDate();
  };

  const shiftStrip = (deltaDays: number) => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() + deltaDays);
    if (d.getTime() < minDate.getTime()) return;
    setAnchorDate(d);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="booking-subhead" data-slot="booking-subhead">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="booking-subhead-title">Select a provider</h2>
        <span className="w-10" aria-hidden />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-4">
          {/* First / Follow Up tabs */}
          <div className="sched-toggle">
            {([
              { value: 'ROUTINE',  label: 'First' },
              { value: 'FOLLOWUP', label: 'Follow Up' },
            ] as const).map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className="sched-toggle-item"
                data-on={tab === t.value || undefined}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
            <input
              ref={inputRef}
              placeholder="Search specialties or providers"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              maxLength={100}
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-border-default bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:border-brand-primary focus:bg-surface-card focus:outline-none"
            />
          </div>

          {/* Date picker header + strip */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <button type="button" onClick={() => shiftStrip(-5)} className="icon-action" aria-label="Previous days">
                <ChevronLeft className="size-4" />
              </button>
              <DatePicker
                value={selectedDate}
                onChange={(d) => {
                  if (d) setSelectedDate(startOfDay(d));
                }}
                minDate={minDate}
                dateFormat={DATE_FORMAT}
                trigger={
                  <button
                    type="button"
                    className="sched-month-btn"
                  >
                    <span>{formatPickerDate(selectedDate, DATE_FORMAT)}</span>
                    <ChevronDown className="size-4" />
                  </button>
                }
              />
              <button type="button" onClick={() => shiftStrip(5)} className="icon-action" aria-label="Next days">
                <ChevronRight className="size-4" />
              </button>
            </div>
            <div className="grid grid-cols-5 gap-1">
              {stripDays.map((d) => {
                const isSelected = isSameDay(d, selectedDate);
                const isPast = d.getTime() < minDate.getTime();
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    disabled={isPast}
                    onClick={() => setSelectedDate(d)}
                    className="sched-day"
                    data-selected={isSelected || undefined}
                    data-off={isPast || undefined}
                  >
                    <span className="sched-day-meta">{MONTH_SHORT[d.getMonth()]}</span>
                    <span className="sched-day-num">{d.getDate()}</span>
                    <span className="sched-day-meta">{DAY_NAMES[d.getDay()]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Provider list */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-grey-200 animate-pulse" />
              ))}
            </div>
          ) : providers.length === 0 ? (
            <p className="sched-none" data-center="">Provider not found</p>
          ) : (
            <div className="space-y-4">
              {providers.map((p, i) => (
                <ProviderRow
                  key={p.id || `provider-${i}`}
                  provider={p}
                  locationName={location?.name ?? ''}
                  onSlotClick={(slot) => onSelection(p, slot)}
                  onMore={() => onMore(p)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ProviderRowProps {
  provider: ProviderSlotsResponse;
  locationName: string;
  onSlotClick: (slot: AppointmentSlot) => void;
  onMore: () => void;
}

function ProviderRow({ provider, locationName, onSlotClick, onMore }: ProviderRowProps) {
  const slots = (provider.slots ?? []).slice(0, 2);
  const hasSlots = slots.length > 0;

  return (
    // Visual-only frame matching the inner slot card shape (rounded-xl
    // border-border-default). Not clickable itself — selection still
    // happens via the slot buttons / arrow inside.
    <div className="sched-prow">
      <div className="sched-prow-top">
        <Avatar
          size="sm"
          className="flex-shrink-0"
          src={provider.photo ?? undefined}
          name={provider.name}
          initials={getInitials(provider.name)}
        />
        <div className="sched-grow">
          <p className="sched-name">
            {provider.name.replace(/^Dr\.?\s*/i, 'Dr. ')}
          </p>
          <p className="sched-sub">
            {specialityDisplay(provider.speciality) ?? 'N/A'}
          </p>
          <p className="sched-sub">
            {formatWorkingHours(provider.workingHours)}
          </p>
        </div>
      </div>

      {hasSlots ? (
        <div className="sched-slot-row">
          <div className="sched-slot-cards">
            {slots.map((slot, i) => (
              <button
                key={`${slot.start}-${i}`}
                type="button"
                onClick={() => onSlotClick(slot)}
                className="sched-slot-card"
              >
                <span className="sched-sub">
                  {fmtSlotDate(slot.start ?? '')}
                </span>
                <span className="sched-slot-time">
                  {fmt12h(slot.start ?? '')}
                </span>
                <span className="sched-slot-loc">
                  {locationName}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onMore}
            className="sched-more"
            aria-label="More slots"
          >
            <ArrowRight className="size-4" />
          </button>
        </div>
      ) : (
        <div className="sched-slot-row">
          <p className="sched-none">
            {NO_SLOT_AVAILABILITY_MESSAGE}
          </p>
          <button
            type="button"
            onClick={onMore}
            className="sched-more"
            aria-label="More slots"
          >
            <ArrowRight className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}
